/**
 * RegistrationWizard
 *
 * Story 11: Registration Wizard Steps
 *
 * 5-step wizard for provider registration:
 * 1. Invite code validation
 * 2. Basic information
 * 3. Service areas
 * 4. License information
 * 5. Review and submit
 *
 * Features:
 * - Step navigation (back/next)
 * - Progress indicator
 * - Form state persistence (localStorage)
 * - Validation on each step
 */

import { useState, useEffect } from 'react';
import { InviteCodeStep } from './InviteCodeStep';
import { BasicInfoStep, type BasicInfoData } from './BasicInfoStep';
import { ServiceAreasStep, type ServiceAreasData } from './ServiceAreasStep';
import { LicenseStep, type LicenseData } from './LicenseStep';
import { ReviewStep } from './ReviewStep';
import { useProviderRegistration } from '../../hooks/useProviderRegistration';
import type { RegistrationFormData } from '../../types';
import { safeJsonParse, isPlainObject } from '@/lib/utils';

const STORAGE_KEY = 'registrationFormData';

const STEPS = [
  'Invite Code',
  'Basic Info',
  'Service Areas',
  'License',
  'Review',
];

export function RegistrationWizard() {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<Partial<RegistrationFormData>>({});
  const [primaryCounty, setPrimaryCounty] = useState<string>('');

  const { mutateAsync: submitRegistration, isPending: isSubmitting } = useProviderRegistration();

  // Load saved form data on mount
  useEffect(() => {
    const savedData = localStorage.getItem(STORAGE_KEY);
    if (savedData) {
      const parsed = safeJsonParse(savedData, isPlainObject, {});
      if (parsed.formData && isPlainObject(parsed.formData)) {
        setFormData(parsed.formData);
      }
      if (typeof parsed.currentStep === 'number') {
        setCurrentStep(parsed.currentStep);
      }
      if (typeof parsed.primaryCounty === 'string') {
        setPrimaryCounty(parsed.primaryCounty);
      }
    }
  }, []);

  // Save form data whenever it changes
  useEffect(() => {
    if (Object.keys(formData).length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        formData,
        currentStep,
        primaryCounty,
      }));
    }
  }, [formData, currentStep, primaryCounty]);

  const handleInviteCodeNext = (code: string) => {
    setFormData(prev => ({ ...prev, inviteCode: code }));
    // In a real implementation, we would get the county from the validation result
    // For now, we'll need to pass it through the InviteCodeStep component
    setCurrentStep(1);
  };

  const handleBasicInfoNext = (data: BasicInfoData) => {
    setFormData(prev => ({
      ...prev,
      companyName: data.companyName,
      contactEmail: data.contactEmail,
      contactPhone: data.contactPhone,
      password: data.password,
    }));
    setCurrentStep(2);
  };

  const handleServiceAreasNext = (data: ServiceAreasData) => {
    setFormData(prev => ({
      ...prev,
      primaryCounty: data.primaryCounty,
      additionalCounties: data.additionalCounties,
      serviceTypes: data.serviceTypes,
    }));
    setPrimaryCounty(data.primaryCounty);
    setCurrentStep(3);
  };

  const handleLicenseNext = (data: LicenseData) => {
    setFormData(prev => ({
      ...prev,
      licenseNumber: data.licenseNumber,
      insuranceInfo: data.insuranceInfo,
      certifications: data.certifications,
    }));
    setCurrentStep(4);
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(0, prev - 1));
  };

  const handleEdit = (step: number) => {
    setCurrentStep(step);
  };

  const handleSubmit = async () => {
    if (!isValidFormData(formData)) {
      throw new Error('Form data is incomplete');
    }

    await submitRegistration(formData as RegistrationFormData);
  };

  const progress = ((currentStep + 1) / STEPS.length) * 100;

  return (
    <div className="max-w-3xl mx-auto py-8 px-4">
      {/* Progress Indicator */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-2">
          <h1 className="text-3xl font-bold text-neutral-900">Provider Registration</h1>
          <span className="text-sm text-neutral-600">
            Step {currentStep + 1} of {STEPS.length}
          </span>
        </div>
        <div className="w-full bg-neutral-200 rounded-full h-2 overflow-hidden">
          <div
            className="bg-primary-600 h-2 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex justify-between mt-2">
          {STEPS.map((step, index) => (
            <span
              key={step}
              className={`text-xs ${
                index <= currentStep ? 'text-primary-600 font-medium' : 'text-neutral-400'
              }`}
            >
              {step}
            </span>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
        {currentStep === 0 && (
          <InviteCodeStep
            onNext={handleInviteCodeNext}
            initialCode={formData.inviteCode}
          />
        )}

        {currentStep === 1 && (
          <BasicInfoStep
            onNext={handleBasicInfoNext}
            onBack={handleBack}
            initialData={{
              companyName: formData.companyName,
              contactName: '', // We need to add this field
              contactEmail: formData.contactEmail,
              contactPhone: formData.contactPhone,
              password: '',
              confirmPassword: '',
            }}
          />
        )}

        {currentStep === 2 && (
          <ServiceAreasStep
            onNext={handleServiceAreasNext}
            onBack={handleBack}
            primaryCounty={formData.primaryCounty || 'Los Angeles'} // Default or from invite validation
            initialData={{
              primaryCounty: formData.primaryCounty,
              additionalCounties: formData.additionalCounties,
              serviceTypes: formData.serviceTypes,
            }}
          />
        )}

        {currentStep === 3 && (
          <LicenseStep
            onNext={handleLicenseNext}
            onBack={handleBack}
            initialData={{
              licenseNumber: formData.licenseNumber,
              insuranceInfo: formData.insuranceInfo,
              certifications: formData.certifications,
            }}
          />
        )}

        {currentStep === 4 && isValidFormData(formData) && (
          <ReviewStep
            data={formData as RegistrationFormData}
            onBack={handleBack}
            onEdit={handleEdit}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
          />
        )}
      </div>
    </div>
  );
}

function isValidFormData(data: Partial<RegistrationFormData>): data is RegistrationFormData {
  return !!(
    data.inviteCode &&
    data.companyName &&
    data.contactEmail &&
    data.contactPhone &&
    data.password &&
    data.primaryCounty &&
    data.serviceTypes &&
    data.serviceTypes.length > 0
  );
}
